import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MarkdownViewerComponent } from '@examdojo/core/ui/markdown-viewer';
import { fromEvent, merge } from 'rxjs';
import { debounceTime, filter, switchMap, tap, throttleTime } from 'rxjs/operators';
import { ContentBlock } from '../../model';
import { ContentBlockTocItem } from './content-block-toc.model';
import { extractHeadingsFromBlocks, TOC_ID_DATA_ATTR } from './extract-headings.util';

interface TocItemWithState extends ContentBlockTocItem {
  isActive: boolean;
  isScrolledOver: boolean;
  itemIndex: number;
}

@Component({
  selector: 'dojo-content-block-toc',
  templateUrl: './content-block-toc.component.html',
  styleUrl: './content-block-toc.component.scss',
  host: { class: 'text-sm flex flex-col text-decoration-none' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, MarkdownViewerComponent],
})
export class ContentBlockTocComponent {
  constructor() {
    merge(this.setInitialActiveHeading(), this.updateActiveHeadingOnScroll()).pipe(takeUntilDestroyed()).subscribe();
  }

  readonly blocks = input.required<ContentBlock[]>();
  readonly scrollableParent = input<HTMLElement>();

  private readonly activeHeadingId = signal<string | null>(null);

  readonly tocItems = computed<ContentBlockTocItem[]>(() => extractHeadingsFromBlocks(this.blocks()));

  readonly tocItemsWithState = computed<TocItemWithState[]>(() => {
    const items = this.tocItems();
    const activeId = this.activeHeadingId();

    if (!items.length) {
      return [];
    }

    const hasActiveItem = activeId !== null;
    return items.map((item, index) => ({
      ...item,
      itemIndex: index,
      isActive: hasActiveItem ? item.id === activeId : index === 0,
      isScrolledOver: this.isScrolledOver(item.id, activeId, items),
    }));
  });

  onItemClick(id: string): void {
    const scrollableParent = this.scrollableParent();
    if (!scrollableParent) {
      return;
    }

    const element = scrollableParent.querySelector(`[${TOC_ID_DATA_ATTR}="${id}"]`);
    if (!element) {
      return;
    }

    const scrollableParentRect = scrollableParent.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const scrollOffset = 16;
    const targetPosition = elementRect.top - scrollableParentRect.top + scrollableParent.scrollTop - scrollOffset;

    scrollableParent.scrollTo({
      top: targetPosition,
      behavior: 'smooth',
    });
  }

  private setInitialActiveHeading() {
    return toObservable(this.tocItems).pipe(
      filter((items) => items.length > 0),
      debounceTime(0), // ensure DOM is ready
      tap(() => this.updateActiveHeading()),
    );
  }

  private updateActiveHeadingOnScroll() {
    return toObservable(this.scrollableParent).pipe(
      filter(Boolean),
      switchMap((parent) =>
        fromEvent(parent, 'scroll').pipe(
          throttleTime(100, undefined, { leading: true, trailing: true }),
          tap(() => this.updateActiveHeading()),
        ),
      ),
    );
  }

  private updateActiveHeading(): void {
    const scrollableParent = this.scrollableParent();
    const items = this.tocItems();

    if (!scrollableParent || !items.length) {
      return;
    }

    const scrollableParentRect = scrollableParent.getBoundingClientRect();
    const scrollTop = scrollableParent.scrollTop;

    // Find the heading that is currently at the top of the viewport
    let activeId: string | null = null;
    let closestDistance = Infinity;

    for (const item of items) {
      const element = scrollableParent.querySelector(`[${TOC_ID_DATA_ATTR}="${item.id}"]`);
      if (!element) {
        continue;
      }

      const elementRect = element.getBoundingClientRect();
      const elementTop = elementRect.top - scrollableParentRect.top;

      // Consider a heading "active" if it's at or above the top of the viewport
      // with a small threshold to account for the heading being slightly below the top
      const threshold = 50;
      if (elementTop <= threshold) {
        // This heading has been scrolled past or is at the top
        if (Math.abs(elementTop) < closestDistance) {
          closestDistance = Math.abs(elementTop);
          activeId = item.id;
        }
      }
    }

    // If no heading is at the top, check if we're at the very top and select the first heading
    if (activeId === null && scrollTop < 50 && items.length > 0) {
      activeId = items[0].id;
    }

    this.activeHeadingId.set(activeId);
  }

  private isScrolledOver(itemId: string, activeId: string | null, items: ContentBlockTocItem[]): boolean {
    if (activeId === null) {
      return false;
    }

    const itemIndex = items.findIndex((i) => i.id === itemId);
    const activeIndex = items.findIndex((i) => i.id === activeId);

    return itemIndex < activeIndex;
  }
}
